/**
 * 67.75, supplies as containers (DECISIONS 224 (5)): the loads drawn — a pallet of every kind (constants/logistics.ts PALLETS),
 * the drop crate the utility drone carries, and a container packed by CONTAINER_PACKINGS (shown in an open frame so its load
 * reads). Built from the ships' kit (one spec, merged, the paint per vertex), Lambert like the frames: one draw a load, a few
 * hundred triangles. Presentation only (design_sheets/supplies.html; the motor pool's containers stay closed in play).
 * A pallet's origin is the middle of its base on the ground, its 1.2 m length along x, its 0.8 m width along z.
 */
import * as THREE from 'three';
import type { ContainerPacking, PalletKind } from '../types';
import { PALLET, PALLETS, DROP_CRATE, CONTAINER_SPOTS, CONTAINER_PACKINGS } from '../constants/logistics';
import { Spec, mergedTier, FRAME, type Livery, type Paint } from './orbitShips';
import { FRAME_DECK } from './vehicleFrame';
import type { UnitPalette } from './unitPalette';

const L = PALLET.length, W = PALLET.width, BASE = 0.14;

/** The wooden pallet: three runners, the deck boards. */
function pallet(s: Spec, l = L, w = W): void {
  for (const z of [-1, 0, 1]) s.box(l, 0.09, 0.1, 0, 0.045, z * (w / 2 - 0.05), 'tan');
  for (let k = 0; k < 7; k++) s.box(0.12, 0.03, w, -l / 2 + 0.06 + (k * (l - 0.12)) / 6, 0.115, 0, 'tan');
}
/** Two straps over a load `h` tall, across its width. */
function straps(s: Spec, h: number, l = L, w = W): void {
  for (const x of [-l / 4, l / 4]) { s.box(0.05, 0.01, w + 0.02, x, BASE + h + 0.005, 0, 'machDk'); for (const sz of [-1, 1]) s.box(0.05, h, 0.01, x, BASE + h / 2, sz * (w / 2 + 0.005), 'machDk'); }
}
/** A grid of boxes on the pallet: `nx` × `nz` a layer, `ny` layers of `h`, painted by layer and place. */
function stack(s: Spec, nx: number, nz: number, ny: number, h: number, paint: (i: number, j: number, k: number) => Paint, gap = 0.02, l = L, w = W): void {
  const bx = l / nx - gap, bz = w / nz - gap;
  for (let k = 0; k < ny; k++) for (let i = 0; i < nx; i++) for (let j = 0; j < nz; j++)
    s.box(bx, h - gap, bz, -l / 2 + (i + 0.5) * (l / nx), BASE + k * h + h / 2, -w / 2 + (j + 0.5) * (w / nz), paint(i, j, k));
}

function load(s: Spec, kind: PalletKind): void {
  const H = PALLET.height - BASE;
  switch (kind) {
    case 'supply':   // the supply dump's: crates three high, olive and tan, under straps
      pallet(s); stack(s, 2, 2, 3, H / 3, (i, j, k) => (i + j + k) % 2 ? 'olive' : 'tan'); straps(s, H); break;
    case 'medical':   // cream cases, a cross in the company's colour on the long faces (not red: a protected emblem)
      pallet(s); stack(s, 2, 1, 3, H / 3, () => 'cream');
      for (const sz of [-1, 1]) for (const x of [-L / 4, L / 4]) { s.box(0.24, 0.07, 0.01, x, BASE + H / 2, sz * (W / 2 + 0.005), 'team'); s.box(0.07, 0.24, 0.01, x, BASE + H / 2, sz * (W / 2 + 0.005), 'team'); }
      straps(s, H); break;
    case 'smallArms':   // ammunition cans, olive, a caution band round the top layer
      pallet(s); stack(s, 4, 3, 4, H / 4, () => 'olive', 0.03); s.box(L + 0.01, 0.05, W + 0.01, 0, BASE + H - 0.12, 0, 'caution'); straps(s, H); break;
    case 'shells':   // long wooden shell crates, three across, three high, a hazard band
      pallet(s); stack(s, 1, 3, 3, H / 3, () => 'tan', 0.03);
      for (const sz of [-1, 1]) s.box(0.12, H - 0.04, 0.01, L / 2 - 0.2, BASE + H / 2, sz * (W / 2 + 0.005), 'hazard');
      straps(s, H); break;
    case 'ordnance':   // LAW tube crates below, grenade and 40 mm boxes above, a hazard band
      pallet(s); stack(s, 1, 4, 2, H / 4, () => 'olive', 0.03);
      for (let i = 0; i < 3; i++) for (let j = 0; j < 2; j++) s.box(L / 3 - 0.03, H / 2 - 0.03, W / 2 - 0.03, -L / 2 + (i + 0.5) * (L / 3), BASE + H / 2 + H / 4, -W / 2 + (j + 0.5) * (W / 2), (i + j) % 2 ? 'tan' : 'slate');
      s.box(L + 0.01, 0.06, W + 0.01, 0, BASE + H / 2, 0, 'hazard'); straps(s, H); break;
    case 'material':   // timber and steel sheet: planks in layers, two sheets on top
      pallet(s);
      for (let k = 0; k < 6; k++) for (let j = 0; j < 4; j++) s.box(L - 0.04, H / 8 - 0.015, W / 4 - 0.02, 0, BASE + (k + 0.5) * (H / 8), -W / 2 + (j + 0.5) * (W / 4), k % 2 ? 'tan' : 'mach');
      for (let k = 0; k < 2; k++) s.box(L, 0.02, W, 0, BASE + 6 * (H / 8) + 0.02 + k * 0.03, 0, 'metal');
      straps(s, 6 * (H / 8) + 0.06); break;
    case 'fobKit': {   // four spots (2.4 × 1.6): Hesco flat-packs — wire mesh and beige liner — and sandbag bales
      const l = 2 * L, w = 2 * W;
      for (const sx of [-1, 1]) for (const sz of [-1, 1]) s.within(new THREE.Matrix4().makeTranslation(sx * L / 2, 0, sz * W / 2), () => pallet(s));
      for (let k = 0; k < 6; k++) s.box(l - 0.1, 0.13, w - 0.4, 0, BASE + 0.07 + k * 0.14, -0.2, k % 2 ? 'tan' : 'metal');
      for (let i = 0; i < 4; i++) s.box(l / 4 - 0.04, 0.5, 0.34, -l / 2 + (i + 0.5) * (l / 4), BASE + 0.25, w / 2 - 0.19, 'tan');
      straps(s, 0.86, l, w); break;
    }
    case 'hmgKit':   // the gun's long case, the tripod's case, belt boxes
      pallet(s); s.box(L - 0.05, 0.3, 0.32, 0, BASE + 0.15, -W / 2 + 0.18, 'machDk'); s.box(L * 0.7, 0.26, 0.3, -0.15, BASE + 0.13, W / 2 - 0.17, 'olive');
      for (let i = 0; i < 4; i++) s.box(0.24, 0.2, 0.3, -0.42 + i * 0.28, BASE + 0.4, 0, 'olive');
      s.box(0.22, 0.02, 0.28, 0, BASE + 0.31, -W / 2 + 0.18, 'caution'); straps(s, 0.5); break;
    case 'relays':   // four mast cases and the head's case
      pallet(s); for (let j = 0; j < 4; j++) s.box(L - 0.06, 0.2, 0.17, 0, BASE + 0.1, -W / 2 + (j + 0.5) * (W / 4), 'olive');
      s.box(0.6, 0.3, W - 0.1, 0, BASE + 0.35, 0, 'teamDk'); s.cyl(0.03, 0.4, 'y', 0.2, BASE + 0.7, 0, 'metal', { seg: 6 }); straps(s, 0.5); break;
    case 'beacons':   // four cases, the company's lids
      pallet(s); stack(s, 2, 2, 1, 0.42, () => 'cream'); for (const x of [-L / 4, L / 4]) for (const z of [-W / 4, W / 4]) s.box(L / 2 - 0.06, 0.04, W / 2 - 0.06, x, BASE + 0.44, z, 'team'); straps(s, 0.46); break;
    case 'drones':   // drone cases below, charge boxes (hazard) above
      pallet(s); stack(s, 2, 2, 2, 0.4, (_i, _j, k) => (k === 0 ? 'slate' : 'hazard')); straps(s, 0.8); break;
  }
}

export interface LoadLook { mesh: THREE.Mesh; triangles: number }
function looked(spec: Spec, p: UnitPalette): LoadLook {
  const g = mergedTier(spec, p, false);
  g.setAttribute('color', g.getAttribute('paint'));
  g.deleteAttribute('glow');
  return { mesh: new THREE.Mesh(g, new THREE.MeshLambertMaterial({ vertexColors: true })), triangles: g.attributes.position.count / 3 };
}

/** A pallet of a kind (one draw). */
export function buildPallet(kind: PalletKind, p: UnitPalette, livery: Livery): LoadLook {
  const s = new Spec(livery);
  load(s, kind);
  return looked(s, p);
}

/** The drop crate the utility drone carries: the company's crate, a cream band, lifting eyes at its top corners, caution corners. */
export function dropCrateSpec(s: Spec, y = 0): void {
  const { length: l, width: w, height: h } = DROP_CRATE;
  s.box(l, h, w, 0, y + h / 2, 0, 'team');
  s.box(l + 0.01, 0.08, w + 0.01, 0, y + h * 0.62, 0, 'cream');
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    s.box(0.07, h + 0.01, 0.07, sx * (l / 2 - 0.03), y + h / 2, sz * (w / 2 - 0.03), 'caution');
    s.box(0.07, 0.05, 0.02, sx * (l / 2 - 0.08), y + h + 0.025, sz * (w / 2 - 0.08), 'metal');   // (a lifting eye: a loop's plate, not a torus — 1,300 △ for four)
  }
  for (const x of [-0.08, 0.08]) s.box(0.06, 0.02, w + 0.02, x, y + h + 0.01, 0, 'machDk');
}
export function buildDropCrate(p: UnitPalette, livery: Livery): LoadLook {
  const s = new Spec(livery);
  dropCrateSpec(s);
  return looked(s, p);
}

/** Where each pallet of a packing stands in its container: spot by spot (two across, eight along, then the second tier), a four-spot kit a 2 × 2 block. */
export function packedSpots(packing: ContainerPacking): Array<{ pallet: PalletKind; x: number; z: number; tier: number }> {
  const { across, along, tiers } = CONTAINER_SPOTS, out: Array<{ pallet: PalletKind; x: number; z: number; tier: number }> = [];
  const used = new Set<string>(), key = (c: number, r: number, t: number) => `${c}:${r}:${t}`;
  const xOf = (c: number) => (c - (across - 1) / 2) * L, zOf = (r: number) => (r - (along - 1) / 2) * W;
  for (const { pallet: kind, count } of CONTAINER_PACKINGS[packing]) {
    const big = PALLETS[kind].spots === 4;
    for (let n = 0; n < count; n++) {
      let placed = false;
      for (let t = 0; t < tiers && !placed; t++) for (let r = 0; r < along && !placed; r += 1) for (let c = 0; c < across && !placed; c++) {
        const cells = big ? (c === 0 && r + 1 < along ? [[0, r], [1, r], [0, r + 1], [1, r + 1]] : null) : [[c, r]];
        if (!cells || cells.some(([cc, rr]) => used.has(key(cc, rr, t)))) continue;
        if (t > 0 && cells.some(([cc, rr]) => !used.has(key(cc, rr, t - 1)))) continue;   // (nothing stands on air)
        cells.forEach(([cc, rr]) => used.add(key(cc, rr, t)));
        out.push({ pallet: kind, x: big ? 0 : xOf(c), z: big ? (zOf(r) + zOf(r + 1)) / 2 : zOf(r), tier: t });
        placed = true;
      }
    }
  }
  return out;
}

/**
 * A container as packed, drawn in an open frame so its load reads (the walled container in play): the frame and one
 * instanced mesh per pallet kind. Its origin the frame's (the middle of its floor, its length along z).
 */
export function buildPackedContainer(packing: ContainerPacking, frame: THREE.Object3D, p: UnitPalette, livery: Livery): { group: THREE.Group; draws: number; triangles: number; pallets: number } {
  const group = new THREE.Group(), spots = packedSpots(packing);
  group.add(frame);
  let draws = 1, triangles = 0;
  const kinds = [...new Set(spots.map(s => s.pallet))];
  for (const kind of kinds) {
    const mine = spots.filter(s => s.pallet === kind), look = buildPallet(kind, p, livery);
    const im = new THREE.InstancedMesh(look.mesh.geometry, look.mesh.material, mine.length);
    im.name = `packed:${packing}:${kind}`;
    mine.forEach((s, k) => im.setMatrixAt(k, new THREE.Matrix4().makeTranslation(s.x, FRAME_DECK + s.tier * PALLET.height, s.z)));
    group.add(im); draws++; triangles += look.triangles * mine.length;
  }
  return { group, draws, triangles, pallets: spots.length };
}
/** The clear inside a container a packing must fit (metres): two across × eight along × two high of pallets in FRAME's clear. */
export const CONTAINER_CLEAR = { width: FRAME.width - 0.44, length: FRAME.length - 0.44, height: FRAME.height - 0.22 - FRAME_DECK };
